import { ArrowDown, ArrowUp, Plus, X } from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Field } from "@/components/ui/field";
import { Callout } from "@/components/ui/feedback";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { sameValues } from "@/lib/form";
import { useDids, useGateways, useSetDomainOutbound } from "@/lib/queries";
import type { Domain, Gateway, OutboundMode } from "@/lib/types";
import { formatPhoneNumber } from "@/lib/utils";

/**
 * Where a domain's reps dial out.
 *
 * Two modes, and the difference between them is worth being blunt about in the UI, because it is
 * the difference between a call going out on the wrong carrier and a call not going out at all:
 *
 *   Automatic  — whatever trunk is configured. What every domain does until someone changes it.
 *   Chosen     — exactly these trunks, in this order, failing over left to right. If every one
 *                of them is disabled or deleted, calls from this domain stop. They do NOT fall
 *                back to another carrier, because caller ID, rates and CNAM registration belong
 *                to the trunk, not to the platform.
 *
 * Order is a preference, not a set, so the chosen list is built and re-ordered explicitly rather
 * than with checkboxes — a checkbox list cannot say which trunk is tried first.
 *
 * The default caller ID is the third thing on this form and belongs here rather than beside the
 * domain's other settings: it is the other half of "how calls leave this domain", and an
 * operator choosing a carrier is the person who knows what that carrier is registered to
 * present. It is offered as a **picker over the tenant's own numbers, never a text field** —
 * the server refuses anything else (a caller ID the platform does not answer is a callback that
 * reaches nobody), and a field that can only be filled correctly from a list somewhere else is
 * a field that gets a typo.
 *
 * It is a *fallback*, and the copy says so: a rep with a number routed to their phone or their
 * room calls out as that number, decided by the switch's XML gateway (ADR-013 in `platform`).
 * Without that sentence, an operator setting a default here would reasonably expect it to apply
 * to everybody, and then find that half the floor presents something else.
 */
/** Radix's Select cannot hold `""`, so "no default" needs a value of its own. */
const NO_CALLER_ID = "none";

export function DomainOutboundDialog({
  open, onOpenChange, domain,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  domain?: Domain;
}) {
  const toast = useToast();
  const save = useSetDomainOutbound();
  const { data: gateways } = useGateways({ enabled: open });
  // Every number the tenant holds, routed or not: an unrouted one still belongs to them and can
  // still be presented — what makes a number unusable here is not holding it.
  const { data: dids } = useDids({}, { enabled: open });
  const [mode, setMode] = React.useState<OutboundMode>("auto");
  const [chosen, setChosen] = React.useState<number[]>([]);
  // `""` is the sentinel for "no default", because Radix's Select cannot hold an empty value —
  // the "None" row carries this instead, and the submit maps it back to null.
  const [callerId, setCallerId] = React.useState<string>(NO_CALLER_ID);
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open || !domain) return;
    setFormError(null);
    setMode(domain.outbound_routing.mode);
    setChosen(domain.outbound_routing.gateway_ids);
    setCallerId(domain.outbound_routing.caller_id_number ?? NO_CALLER_ID);
  }, [open, domain]);

  const byId = new Map((gateways ?? []).map((g) => [g.id, g]));
  const chosenRows = chosen.map((id) => byId.get(id)).filter(Boolean) as Gateway[];
  // A chosen id whose gateway has vanished from the list entirely — deleted out from under the
  // saved routing. Shown so it can be removed rather than silently dropped on save.
  const orphans = chosen.filter((id) => !byId.has(id));
  const available = (gateways ?? []).filter((g) => !chosen.includes(g.id));
  const automatic = (gateways ?? []).find((g) => g.enabled);
  const saved = domain?.outbound_routing;
  const changed =
    saved !== undefined &&
    !sameValues(
      { mode, chosen, callerId },
      {
        mode: saved.mode,
        chosen: saved.gateway_ids,
        callerId: saved.caller_id_number ?? NO_CALLER_ID,
      },
    );

  function move(index: number, by: number) {
    setChosen((ids) => {
      const next = [...ids];
      const target = index + by;
      if (target < 0 || target >= next.length) return ids;
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    if (!domain) return;
    if (mode === "gateways" && chosen.length === 0) {
      setFormError("Choose at least one gateway, or switch to automatic.");
      return;
    }
    try {
      await save.mutateAsync({
        id: domain.id,
        mode,
        gateway_ids: mode === "gateways" ? chosen : [],
        caller_id_number: callerId === NO_CALLER_ID ? null : callerId,
      });
      toast.success("Outbound routing saved", domain.name);
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the routing.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>Outbound routing</DialogTitle>
          <DialogDescription>
            Which trunks <span className="font-mono">{domain?.name}</span> dials out on, and what
            its reps present when they have no number of their own. Saving re-reads this domain's
            dialplan and its endpoints, so the next call uses it.
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}

            <fieldset className="space-y-2">
              <legend className="text-xs font-medium text-text">Mode</legend>
              <ModeOption
                checked={mode === "auto"}
                onSelect={() => setMode("auto")}
                title="Automatic"
                description={
                  automatic
                    ? `Whatever trunk is configured — right now that is ${automatic.name}.`
                    : "Whatever trunk is configured. There is no enabled trunk yet, so calls out of this domain will not complete."
                }
              />
              <ModeOption
                checked={mode === "gateways"}
                onSelect={() => setMode("gateways")}
                title="Chosen gateways"
                description="Exactly the trunks below, tried in order. If all of them are disabled or deleted, calls from this domain stop rather than moving to another carrier."
              />
            </fieldset>

            <Field
              label="Default caller ID"
              htmlFor="outbound-caller-id"
              hint="A rep with a number pointed at their phone or their room calls out as that number. Everyone else in this domain presents this one."
            >
              <Select value={callerId} onValueChange={setCallerId}>
                <SelectTrigger id="outbound-caller-id">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CALLER_ID}>
                    No default — the trunk decides
                  </SelectItem>
                  {(dids ?? []).map((did) => (
                    <SelectItem key={did.id} value={did.number}>
                      <span className="font-mono">{formatPhoneNumber(did.number)}</span>
                      {did.description && (
                        <span className="ml-1.5 text-text-muted">{did.description}</span>
                      )}
                    </SelectItem>
                  ))}
                  {/* The saved number after the row it names has been released. Kept as a choice
                      so the form opens showing what is actually in effect — a picker that
                      silently dropped it would save a change nobody made. */}
                  {callerId !== NO_CALLER_ID &&
                    !(dids ?? []).some((d) => d.number === callerId) && (
                      <SelectItem value={callerId}>
                        <span className="font-mono">{formatPhoneNumber(callerId)}</span>
                        <span className="ml-1.5 text-warning-11">no longer held</span>
                      </SelectItem>
                    )}
                </SelectContent>
              </Select>
            </Field>

            {mode === "gateways" && (
              <>
                <div className="space-y-2">
                  <p className="text-xs font-medium text-text">
                    In order — the first one is tried first
                  </p>
                  {chosenRows.length === 0 && orphans.length === 0 ? (
                    <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-text-muted">
                      Nothing chosen yet. Add a gateway below.
                    </p>
                  ) : (
                    <ol className="space-y-1.5">
                      {chosenRows.map((g, i) => (
                        <li
                          key={g.id}
                          className="flex items-center gap-2 rounded-md border border-border px-3 py-2"
                        >
                          <span className="w-4 text-xs tabular-nums text-text-subtle">{i + 1}</span>
                          <span className="min-w-0 flex-1">
                            <span className="text-xs font-medium text-text">{g.name}</span>
                            <span className="ml-1.5 font-mono text-xs text-text-muted">
                              {g.realm}
                            </span>
                            {!g.enabled && (
                              <Badge tone="warning" size="sm" className="ml-1.5">
                                Disabled
                              </Badge>
                            )}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Move ${g.name} up`}
                            disabled={i === 0}
                            onClick={() => move(i, -1)}
                          >
                            <ArrowUp />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Move ${g.name} down`}
                            disabled={i === chosenRows.length - 1}
                            onClick={() => move(i, 1)}
                          >
                            <ArrowDown />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Remove ${g.name}`}
                            onClick={() => setChosen((ids) => ids.filter((id) => id !== g.id))}
                          >
                            <X />
                          </Button>
                        </li>
                      ))}
                      {orphans.map((id) => (
                        <li
                          key={id}
                          className="flex items-center gap-2 rounded-md border border-danger-6 px-3 py-2"
                        >
                          <span className="min-w-0 flex-1 text-xs text-danger-11">
                            Gateway {id} no longer exists.
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Remove gateway ${id}`}
                            onClick={() => setChosen((ids) => ids.filter((x) => x !== id))}
                          >
                            <X />
                          </Button>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>

                {available.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-text">Available</p>
                    <ul className="space-y-1.5">
                      {available.map((g) => (
                        <li
                          key={g.id}
                          className="flex items-center gap-2 rounded-md border border-border px-3 py-2"
                        >
                          <span className="min-w-0 flex-1">
                            <span className="text-xs font-medium text-text">{g.name}</span>
                            <span className="ml-1.5 font-mono text-xs text-text-muted">
                              {g.realm}
                            </span>
                            {!g.enabled && (
                              <Badge tone="warning" size="sm" className="ml-1.5">
                                Disabled
                              </Badge>
                            )}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setChosen((ids) => [...ids, g.id])}
                          >
                            <Plus />
                            Add
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={save.isPending}>
              Save routing
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

function ModeOption({
  checked, onSelect, title, description,
}: {
  checked: boolean;
  onSelect: () => void;
  title: string;
  description: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border px-3 py-2.5 has-[:checked]:border-accent-8 has-[:checked]:bg-accent-2">
      <input
        type="radio"
        name="outbound-mode"
        className="mt-0.5 accent-accent-9"
        checked={checked}
        onChange={onSelect}
      />
      <span>
        <span className="block text-xs font-medium text-text">{title}</span>
        <span className="block text-xs text-text-muted">{description}</span>
      </span>
    </label>
  );
}
